import { Button, Col, GetProp, Image, Upload, UploadProps } from "antd";
import useForm from "../../../hooks/useForm";
import { CloseModal } from "../../../hooks/useModal";
import usePageState from "../../../hooks/usePageState";
import {
  CreateItemRequest,
  ItemModel,
  SizeMappingModel,
} from "../../../../infrastructure/model/ProductItemModel";
import AppInputDropdown from "../../../components/AppInputDropdown";
import AppInputNumber from "../../../components/AppInputNumber";
import AppInputText from "../../../components/AppInputText/AppInputText";
import BaseDialog from "../../../components/BaseDialog";
import Column from "../../../components/Column";
import DialogFooter from "../../../components/DialogFooter";
import Row from "../../../components/Row";
import Title from "../../../components/Title";
import {
  ITEM_CATEGORY_DATA,
  ITEM_HOT_SALE_DATA,
  YES_NO_ENUM,
  ITEM_TYPE_ENUM,
  SIZE_MAPPING_OPTION,
  SIZE_MAPPING_ENUM,
  YES_NO_DATA,
} from "../../../../applications/constants/AppConst";
import * as Yup from "yup";
import { DeleteOutlined, PlusCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { useState } from "react";
import AppInputDropdownMultiple from "../../../components/AppDropdownMultiple";
import { useToppingContext } from "../../../context/AppToppingContext";
import AppDropdown from "../../../components/AppDropdown";
import Visible from "../../../components/Visible";
import {
  createItemUseCase,
  updateItemUseCase,
} from "../../../../applications/useCase/ApiUseCase/ItemApiUseCase";

interface CreateItemDialogProps {
  onClose: CloseModal;
  updateItem?: ItemModel;
}

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

function CreateItemDialog(props: CreateItemDialogProps) {
  const { onClose, updateItem } = props;
  const { showClientError, showError, setLoading, isLoading, showSuccess } = usePageState();
  const [fileImage, setFileImage] = useState<File>();
  const { toppings } = useToppingContext();

  async function insertItem(request: CreateItemRequest, imageFile?: File) {
    // kiểm tra có bị trùng size k
    const sizes = request.size_mapping.map((item) => item.size);
    const uniqueSizes = new Set(sizes);
    if (uniqueSizes.size !== sizes.length) {
      showClientError("Trùng lặp size đồ uống");
      return;
    }
    const existEmptySizePrice = request.size_mapping?.find((s) => s.price === 0);
    if (!!existEmptySizePrice) {
      showClientError(
        "Bạn chưa điền đầy đủ giá tiền cho size: " +
          SIZE_MAPPING_OPTION?.find((o) => o.value === existEmptySizePrice.size)?.label
      );
      return;
    }

    let res: ItemModel | undefined = undefined;
    if (updateItem?.id) {
      res = await updateItemUseCase({
        request: { ...request, id: updateItem.id, imageFile },
        setLoading,
        showError,
      });
    } else {
      res = await createItemUseCase({
        request,
        imageFile,
        setLoading,
        showError,
      });
    }
    if (res) {
      onClose(res);
    }
  }

  const sizeMappingSchema = Yup.object({
    size: Yup.number().required("Size là bắt buộc"),
    price: Yup.number().required("Bạn chưa điền đầy đủ giá tiền cho size"),
  });

  const validationSchema = Yup.object({
    name: Yup.string().required("Thiếu tên sản phẩm"),
    size_mapping: Yup.array()
      .of(sizeMappingSchema)
      .min(1, "Phải có ít nhất một loại kích cỡ đồ uống"),
  });

  const formik = useForm<CreateItemRequest>({
    initialValues: updateItem ?? {
      name: "",
      hot_sale: false,
      open_sale: true,
      is_new: false,
      size_mapping: [
        {
          size: SIZE_MAPPING_ENUM.S,
          price: 0,
        },
      ],
      attached: [],
      type: ITEM_TYPE_ENUM.BLACK_MILK_TEA,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => await insertItem(values, fileImage),
  });

  const handleAddMoreSize = () => {
    const oldSizeMapping = formik.values.size_mapping;
    formik.updateFieldValue("size_mapping", [...oldSizeMapping, { size: 0, price: 0 }]);
  };

  const handleUpdateSize = (index: number, key: keyof SizeMappingModel, value: number) => {
    const oldSizeMapping = formik.values.size_mapping;
    const newSizeMapping = [
      ...oldSizeMapping.slice(0, index),
      {
        ...oldSizeMapping[index],
        [key]: value,
      },
      ...oldSizeMapping.slice(index + 1),
    ];
    formik.updateFieldValue("size_mapping", newSizeMapping);
  };

  const handleDeleteSizeMapping = (index: number) => {
    const newSizeMapping = formik.values.size_mapping?.filter((s, i) => i !== index);
    formik.updateFieldValue("size_mapping", newSizeMapping);
  };

  const beforeUpload = (file: FileType) => {
    const isLt2M = file.size / 1024 / 1024 < 5;
    if (!isLt2M) {
      showClientError("Image must smaller than 5MB!");
      return false;
    }
    setFileImage(file);
    return false;
  };

  return (
    <BaseDialog
      title={updateItem?.id ? "Cập nhật sản phẩm" : "Thêm mới sản phẩm"}
      isLoading={isLoading}
      footer={
        <DialogFooter
          submitProps={{
            onClick: () => formik.checkErrorAndSubmit(showClientError),
          }}
          cancelProps={{
            onClick: () => onClose(),
          }}
        />
      }
    >
      <Column className="gap-2 w-100">
        <Row>
          <Title label="Ảnh sản phẩm" />
          <Row className="gap-2">
            <Visible isVisible={!!updateItem?.image}>
              <Image
                src={updateItem?.image}
                alt="Ảnh hiện tại"
                style={{
                  width: "40px",
                  height: "40px",
                }}
              />
            </Visible>
            <Upload
              name="avatar_item"
              className="flex flex-row avatar-uploader"
              accept="image/*"
              showUploadList={false}
              maxCount={1}
              beforeUpload={beforeUpload}
              action={undefined}
            >
              <Button icon={<UploadOutlined />} type="primary">
                Upload
              </Button>
            </Upload>
            <div
              style={{
                maxWidth: `210px`,
                minWidth: "210px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                wordBreak: "break-word",
              }}
            >
              {fileImage?.name}
            </div>
          </Row>
        </Row>
        <AppInputDropdown
          label="Loại sản phẩm"
          options={ITEM_CATEGORY_DATA}
          currentValue={formik.values.type}
          onChangeValue={(e) => formik.updateFieldValue("type", e)}
        />
        <AppInputText
          label="Tên sản phẩm"
          currentValue={formik.values.name}
          isRequired
          onTextChange={(e) => formik.updateFieldValue("name", e)}
          errorText={formik.getFormErrorMessage("name")}
        />

        <Visible
          isVisible={
            ![ITEM_TYPE_ENUM.SMOOTHIE, ITEM_TYPE_ENUM.COFFEE_MATCHA_HOT_DRINK]?.includes(
              formik.values.type
            )
          }
        >
          <AppInputDropdownMultiple
            label="Topping đi kèm"
            options={toppings?.map((e) => {
              return {
                label: e.name,
                value: e.id,
              };
            })}
            currentValue={formik.values.attached}
            onChangeValue={(e) => {
              formik.updateFieldValue("attached", e);
            }}
            errorText={formik.getFormErrorMessage("attached")}
          />
        </Visible>

        <AppInputDropdown
          label="Hot sale"
          options={ITEM_HOT_SALE_DATA}
          currentValue={formik.values.hot_sale ? YES_NO_ENUM.YES : YES_NO_ENUM.NO}
          onChangeValue={(e) => formik.updateFieldValue("hot_sale", e === YES_NO_ENUM.YES)}
        />
        <AppInputDropdown
          label="Sản phẩm mới"
          options={YES_NO_DATA}
          currentValue={formik.values.is_new ? YES_NO_ENUM.YES : YES_NO_ENUM.NO}
          onChangeValue={(e) => formik.updateFieldValue("is_new", e === YES_NO_ENUM.YES)}
        />
        <AppInputDropdown
          label="Mở bán"
          options={YES_NO_DATA}
          currentValue={formik.values.open_sale ? YES_NO_ENUM.YES : YES_NO_ENUM.NO}
          onChangeValue={(e) => formik.updateFieldValue("open_sale", e === YES_NO_ENUM.YES)}
        />
        <Button
          children="Thêm size"
          icon={<PlusCircleOutlined />}
          iconPosition="start"
          color="primary"
          variant="solid"
          style={{ width: "20%" }}
          onClick={handleAddMoreSize}
          disabled={formik.values.size_mapping?.length === 3}
        />
        {formik.values.size_mapping?.map((s, index) => {
          return (
            <Row className="gap-2">
              <Col span={6}>
                <AppDropdown
                  className="w-100"
                  options={SIZE_MAPPING_OPTION}
                  placeholder="Chọn size"
                  currentValue={s.size}
                  onChangeValue={(e) => handleUpdateSize(index, "size", e)}
                />
              </Col>
              <Col span={14}>
                <AppInputNumber
                  isRequired
                  currentValue={s.price}
                  onChangeValue={(e) => handleUpdateSize(index, "price", e)}
                  placeholder="Giá tiền"
                  min={0}
                  addonAfter="đ"
                />
              </Col>
              <Col span={3}>
                <Button
                  icon={<DeleteOutlined />}
                  color="danger"
                  variant="solid"
                  onClick={() => handleDeleteSizeMapping(index)}
                />
              </Col>
            </Row>
          );
        })}
      </Column>
    </BaseDialog>
  );
}
export default CreateItemDialog;
